import { useCallback, useEffect, useRef } from 'react';

interface TrackEventOptions {
  category: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  metadata?: Record<string, any>;
}

interface UseAriaAnalyticsOptions {
  endpoint?: string;
  debug?: boolean;
  disableForForms?: boolean;
  userId?: string; 
}

export function useAriaAnalytics({
  endpoint = '/api/analytics',
  debug = false,
  disableForForms = true,
  userId
}: UseAriaAnalyticsOptions = {}) {
  const initialized = useRef(false);
  const pendingEvents = useRef<TrackEventOptions[]>([]);
  const isFormPage = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      isFormPage.current = window.location.pathname.includes('/contact') || 
                           window.location.pathname.includes('/investor') ||
                           window.location.pathname.includes('/partner');
      
      if (isFormPage.current && debug) {
        console.log('Form page detected, analytics tracking will be limited');
      }
    }
  }, [debug]);

  const trackEvent = useCallback(async (options: TrackEventOptions) => {
    if (disableForForms && isFormPage.current) {
      if (debug) {
        console.log('Skipping analytics tracking on form page:', options);
      }
      return;
    }

    if (!initialized.current) {
      pendingEvents.current.push(options);
      if (debug) {
        console.log('Added event to pending queue:', options);
      }
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        if (debug) {
          console.warn('Analytics tracking timed out after 3 seconds');
        }
      }, 3000);

      const trackEndpoint = `${endpoint}/track`.replace(/^https?:\/\/[^\/]+/, '');
      
      // Prepare request body first to ensure it's valid JSON
      const requestBody = {
        type: options.category.toLowerCase(),
        event: options.action,
        label: options.label,
        value: options.value,
        nonInteraction: options.nonInteraction,
        metadata: {
          ...options.metadata,
          userId: userId, 
          timestamp: new Date().toISOString(),
        },
      };
      
      // Validate that we have a proper JSON object before sending
      const bodyString = JSON.stringify(requestBody);
      if (!bodyString || bodyString === '{}' || bodyString === 'null') {
        throw new Error('Invalid request body generated');
      }
      
      fetch(trackEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin', 
        mode: 'cors', 
        body: bodyString,
        signal: controller.signal,
      })
        .then(response => {
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`Analytics tracking failed: ${response.status}`);
          }
          if (debug) {
            console.log('Analytics event tracked successfully:', options);
          }
          return response.json();
        })
        .catch(error => {
          if (debug) {
            console.error('Analytics tracking error:', error);
          }
        });
    } catch (error) {
      if (debug) {
        console.error('Failed to track analytics event:', error);
      }
    }
  }, [endpoint, debug, disableForForms, userId]); 

  useEffect(() => {
    if (disableForForms && isFormPage.current) {
      if (debug) {
        console.log('Skipping analytics initialization on form page');
      }
      return;
    }

    const initAnalytics = async () => {
      if (initialized.current) return;

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          if (debug) {
            console.warn('Analytics initialization timed out after 3 seconds');
          }
        }, 3000);

        const initEndpoint = `${endpoint}/init`.replace(/^https?:\/\/[^\/]+/, '');

        // Prepare request body first to ensure it's valid JSON
        const requestBody = {
          event: 'init',
          metadata: {
            platform: 'web',
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            userId: userId, 
          },
        };
        
        // Validate that we have a proper JSON object before sending
        const bodyString = JSON.stringify(requestBody);
        if (!bodyString || bodyString === '{}' || bodyString === 'null') {
          throw new Error('Invalid request body generated');
        }

        const response = await fetch(initEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin', 
          mode: 'cors', 
          body: bodyString,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Analytics initialization failed: ${response.status}`);
        }

        initialized.current = true;
        if (debug) {
          console.log('Analytics initialized successfully');
        }

        if (pendingEvents.current.length > 0) {
          if (debug) {
            console.log(`Processing ${pendingEvents.current.length} pending events`);
          }
          
          pendingEvents.current.forEach((event, index) => {
            setTimeout(() => {
              trackEvent(event);
            }, index * 100);
          });
          
          pendingEvents.current = [];
        }
      } catch (error) {
        if (debug) {
          console.error('Error initializing analytics:', error);
        }
        initialized.current = true;
      }
    };

    const timeoutId = setTimeout(() => {
      initAnalytics();
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [endpoint, debug, trackEvent, disableForForms, userId]); 

  return { trackEvent };
}